import React, { useState, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AgeGateProvider, useAgeGate } from './contexts/AgeGateContext';
import ProtectedRoute from './components/ProtectedRoute';
import CannabisLeaderboard from './components/CannabisLeaderboard';
import LandingPage from './components/LandingPage';
import AgeGateModal from './components/AgeGateModal';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancelled from './components/PaymentCancelled';
import FAQ from './components/FAQ';
import TermsOfService from './components/TermsOfService';
import Pricing from './components/Pricing';
import About from './components/About';
import Contact from './components/Contact';
import PrivacyPolicy from './components/PrivacyPolicy';
import { FEATURE_FLAGS } from './utils/config';

function App() {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [, setRegisterPlan] = useState('premium');
  // Simple routing based on URL path
  const currentPath = window.location.pathname;


  // Handle authentication modal controls
  const handleShowLogin = useCallback(() => {
    setShowLogin(true);
    setShowRegister(false);
  }, []);

  const handleShowRegister = useCallback((plan = 'premium') => {
    setRegisterPlan(plan);
    setShowRegister(true);
    setShowLogin(false);
  }, []);

  const handleCloseAuth = useCallback(() => {
    setShowLogin(false);
    setShowRegister(false);
  }, []);

  const handleSwitchToRegister = useCallback(() => {
    setShowLogin(false);
    setShowRegister(true);
  }, []);

  const handleSwitchToLogin = useCallback(() => {
    setShowRegister(false);
    setShowLogin(true);
  }, []);

  return (
    <AgeGateProvider>
      <AuthProvider>
        <div className="App min-h-screen w-full">
          {/* Handle different pages based on URL */}
          {currentPath === '/payment-success' ? (
            <PaymentSuccess />
          ) : currentPath === '/payment-cancelled' ? (
            <PaymentCancelled />
          ) : currentPath === '/faq' ? (
            <FAQ onBack={() => window.location.href = '/'} />
          ) : currentPath === '/terms' ? (
            <TermsOfService onBack={() => window.location.href = '/'} />
          ) : currentPath === '/pricing' ? (
            <Pricing onSelectPlan={handleShowRegister} onBack={() => window.location.href = '/'} />
          ) : currentPath === '/about' ? (
            <About onBack={() => window.location.href = '/'} />
          ) : currentPath === '/contact' ? (
            <Contact onBack={() => window.location.href = '/'} />
          ) : currentPath === '/privacy' ? (
            <PrivacyPolicy onBack={() => window.location.href = '/'} />
          ) : FEATURE_FLAGS.landingPageActive ? (
            // Landing page mode - show landing page or protected leaderboard
            <AppWithLandingPage 
              onSignIn={handleShowLogin}
              onSignUp={handleShowRegister}
            />
          ) : (
            // Development/testing mode - show leaderboard directly
            <CannabisLeaderboard />
          )}

          {/* Auth Modals - don't show on payment pages */}
          {!currentPath.includes('/payment-') && (
            <>
              {showLogin && (
                <Login 
                  onClose={handleCloseAuth}
                  onSwitchToRegister={handleSwitchToRegister}
                />
              )}
              
              {showRegister && (
                <Register 
                  onClose={handleCloseAuth}
                  onSwitchToLogin={handleSwitchToLogin}
                />
              )}
            </>
          )}
        </div>
      </AuthProvider>
    </AgeGateProvider>
  );
}

// Component to handle landing page vs leaderboard display logic
const AppWithLandingPage = ({ onSignIn, onSignUp }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isVerified, isLoading: ageGateLoading, isDenied, confirmAge, denyAge } = useAgeGate();

  // Show loading screen while checking authentication and age verification
  if (isLoading || ageGateLoading) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50  flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is authenticated, they ALREADY paid and have full access
  // Skip age gate for authenticated users as they've already provided payment info
  if (isAuthenticated) {
    return (
      <ProtectedRoute requireAuth={true}>
        <CannabisLeaderboard />
      </ProtectedRoute>
    );
  }

  // If age verification was denied, show denial page
  if (isDenied) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-red-50 via-pink-50 to-orange-50  flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-2xl shadow-xl">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-2xl font-bold text-red-600">21+</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Access Denied
          </h1>
          <p className="text-gray-600 mb-6">
            You must be 21 or older to access this website. This site contains information about cannabis products intended for adults only.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = 'https://www.samhsa.gov/find-help/national-helpline'}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Get Help & Resources
            </button>
            <button
              onClick={() => window.location.href = 'https://www.google.com'}
              className="w-full border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Go to Google
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If user is not age verified, show age gate modal
  if (!isVerified) {
    return (
      <>
        {/* Show a minimal background while age gate is displayed */}
        <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50  flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-green-600">🌿</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">WeederBoard</h1>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
        <AgeGateModal 
          onConfirm={confirmAge}
          onDeny={denyAge}
        />
      </>
    );
  }

  // If user is age verified but not authenticated, show the landing page
  return (
    <LandingPage 
      onSignIn={onSignIn}
      onSignUp={onSignUp}
    />
  );
};

export default App;
